import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tracks whether a screen's 3D board has come up, so the `SquareOverlay` above
 * it can stay inert until it has.
 *
 * Pair with `<Canvas onCreated={markReady}>`. R3F fires `onCreated` once the
 * WebGL context and renderer exist, which is the bulk of the wait — measured at
 * roughly 300ms from "start the game" to a drawn board, nearly all of it
 * context creation and shader compile.
 *
 * `onCreated` alone is one frame AHEAD of the first pixels, and that gap was
 * not free. Two `chess-mate` board captures rewrote themselves between
 * regeneration runs on the CI runner with nothing touching those screens —
 * real pixel variance, under the 0.5% threshold so it passed, but the kind
 * that reddens a shard on a step nobody edited. `data-board-ready` proved the
 * canvas had reported, not that anything had been drawn into it.
 *
 * So the flag is raised after a painted frame rather than on creation. Two
 * `requestAnimationFrame` hops: the first callback runs before the paint of
 * the frame R3F renders into, the second before the paint after it — by which
 * point those pixels are on screen. It is the standard after-paint idiom and
 * it keeps the whole signal inside `onCreated`, so no `useFrame` and no
 * three.js in a screen test.
 *
 * Waiting longer is safe in the direction that matters: the overlay is inert
 * for one extra frame, and the fallback below is unchanged.
 *
 * Not reproducible on a developer's macOS machine — a real GPU draws the board
 * fast enough to close the gap. The runner renders in software, which is where
 * it showed.
 *
 * The fallback timer is why this is a hook rather than a bare `useState` in
 * each screen. If `onCreated` never fires — no WebGL context, a driver that
 * refuses — the overlay would stay disabled and the game would be silently
 * unplayable. A board that is slow to draw is a blemish; a board that can never
 * be tapped is a broken game, so the timer always wins eventually.
 */
export function useBoardReady(fallbackMs = 2000): readonly [boolean, () => void] {
  const [ready, setReady] = useState(false);
  const frames = useRef<number[]>([]);

  /**
   * Call from `onCreated`. Resolves after a painted frame, not on the call.
   *
   * `requestAnimationFrame` is guarded because this also runs in screen tests
   * and in any environment without one; there, the flag is set immediately and
   * the behaviour is what it was before this change.
   */
  const markReady = useCallback(() => {
    if (typeof requestAnimationFrame !== 'function') {
      setReady(true);
      return;
    }
    frames.current.push(
      requestAnimationFrame(() => {
        frames.current.push(requestAnimationFrame(() => setReady(true)));
      }),
    );
  }, []);

  useEffect(() => {
    if (ready) return;
    const id = setTimeout(() => setReady(true), fallbackMs);
    return () => clearTimeout(id);
  }, [ready, fallbackMs]);

  // Cancel anything still pending if the screen goes away mid-handshake.
  useEffect(
    () => () => {
      if (typeof cancelAnimationFrame === 'function') {
        for (const f of frames.current) cancelAnimationFrame(f);
      }
      frames.current = [];
    },
    [],
  );

  return [ready, markReady] as const;
}
